import { ChangeDetectionStrategy, Component, computed, contentChild, input, output } from '@angular/core';
import type {
  CompanyColumnDef,
  CompanyColumnEditorKind,
  CompanyColumnFilterOption,
  CompanyColumnPinned,
  CompanyColumnRendererKind,
  CompanyColumnSelectOption,
} from '../core/interfaces/grid-column.types';
import { CompanyCellDefDirective } from './cell-def.directive';
import { CompanyCellEditDefDirective } from './cell-edit-def.directive';

/**
 * Definição declarativa de coluna. É um Component (não Directive) porque
 * `<company-column>` aceita filhos (`<ng-template companyCellDef>`).
 * Não renderiza nada — é lido pelo Host via `contentChildren`.
 */
@Component({
  selector: 'company-column',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: '',
})
export class CompanyColumnComponent<T = unknown> {
  readonly field = input.required<string>();
  readonly headerName = input<string>();
  readonly width = input<number>();
  readonly flex = input<number>();
  readonly minWidth = input<number>();
  readonly maxWidth = input<number>();
  readonly pinned = input<CompanyColumnPinned>(null);

  // ── Interação ───────────────────────────────────────────────────────────
  readonly sortable = input(false);
  /** Função de comparação client-side para ordenação. Se omitida e sortable=true, usa padrão. */
  readonly sortFn = input<(a: T, b: T) => number>();
  readonly resizable = input(true);
  /** Habilita filtro no FilterPlugin global. */
  readonly filterable = input(false);
  /** Opções para dropdown de filtro nativo da coluna. */
  readonly filterOptions = input<ReadonlyArray<CompanyColumnFilterOption>>();
  /** Função client-side chamada com os valores selecionados no filtro. */
  readonly filterFn = input<(selectedValues: unknown[], item: T) => boolean>();
  /** Múltiplos filtros simultâneos (padrão: true). */
  readonly filterMultiple = input<boolean>(true);

  // ── Edição ──────────────────────────────────────────────────────────────
  readonly editable = input(false);
  readonly renderer = input<CompanyColumnRendererKind>();
  readonly editor = input<CompanyColumnEditorKind>();
  readonly rendererParams = input<Record<string, unknown>>();
  readonly selectOptions = input<readonly CompanyColumnSelectOption[]>();
  readonly valueFormatter = input<(value: unknown, row: T) => string>();

  readonly cellSave = output<{
    row: T;
    field: string;
    previousValue: unknown;
    newValue: unknown;
  }>();

  private readonly cellDef = contentChild(CompanyCellDefDirective);
  private readonly cellEditDef = contentChild(CompanyCellEditDefDirective);

  readonly columnDef = computed<CompanyColumnDef<T>>(() => ({
    field: this.field(),
    headerName: this.headerName(),
    width: this.width(),
    flex: this.flex(),
    minWidth: this.minWidth(),
    maxWidth: this.maxWidth(),
    pinned: this.pinned(),
    sortable: this.sortable(),
    sortFn: this.sortFn(),
    resizable: this.resizable(),
    filterable: this.filterable(),
    filterOptions: this.filterOptions(),
    filterFn: this.filterFn(),
    filterMultiple: this.filterMultiple(),
    editable: this.editable(),
    renderer: this.cellDef() ? 'template' : this.renderer(),
    editor: this.editor(),
    rendererParams: this.rendererParams(),
    selectOptions: this.selectOptions(),
    valueFormatter: this.valueFormatter(),
    cellTemplate: this.cellDef()?.template,
    cellEditTemplate: this.cellEditDef()?.template,
  }));
}
